import { useEffect, useRef, useState } from "react";
import { Button, Form, FormField, List, Modal, Radio, Segment, Table } from "semantic-ui-react";
import AddNewVersionToDocumentModalContainer from "../containers/AddNewVersionToDocumentModalContainer";
import { Document } from "../models/Document";
import { toastError } from "../utils";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitInProgress: boolean;
  title?: string;
  showAsBuilt?: boolean;
  documents?: Document[];
  onAsBuiltAdded?: () => void;
  showGisDeclaration: boolean;
  asBuiltCompletionDeclarations?: string[] | null;
}

const CompletePermitModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  title,
  showAsBuilt,
  documents,
  onAsBuiltAdded,
  showGisDeclaration,
  asBuiltCompletionDeclarations,
}: Props) => {
  const [selectedDocumentToUploadAsBuilt, setSelectedDocumentToUploadAsBuilt] = useState<Document | null>(null);
  const declarationInitialized = useRef(false);
  const [asBuiltDeclarations, setAsBuiltDeclarations] = useState<
    { index: number; text: string; value: boolean; readonly: boolean }[]
  >([]);

  useEffect(() => {
    if (!open) {
      declarationInitialized.current = false;
      setAsBuiltDeclarations([]);
      setSelectedDocumentToUploadAsBuilt(null);
      return;
    }

    if (declarationInitialized.current) {
      return;
    }

    if (asBuiltCompletionDeclarations?.length) {
      setAsBuiltDeclarations(
        asBuiltCompletionDeclarations.map((d, i) => ({
          index: i,
          text: d,
          value: false,
          readonly: false,
        })),
      );
      declarationInitialized.current = true;
    }
  }, [open, asBuiltCompletionDeclarations]);

  const handleSubmit = () => {
    const allDeclarationsChecked = asBuiltDeclarations.every((item) => item.value === true);
    if (showAsBuilt && !allDeclarationsChecked) {
      toastError("Please confirm the declaration before marking the permit as completed.");
      return;
    }

    onSubmit();
  };

  const handleChange = (value: boolean, i: number) => {
    const newValues = [...asBuiltDeclarations];
    newValues[i].value = value ?? false;
    setAsBuiltDeclarations(newValues);
  };

  const handleClose = () => {
    onClose();
  };

  const renderDocTableRow = () => {
    if (documents && documents.length > 0) {
      return documents.map((doc) => (
        <Table.Row key={doc.id}>
          <Table.Cell>
            {doc.title}, version {doc.versionNo}
          </Table.Cell>
          <Table.Cell>
            {doc.versionNo === doc.latest.versionNo ? (
              <span>
                "Not uploaded"{" "}
                <Button
                  size="mini"
                  onClick={() => {
                    setSelectedDocumentToUploadAsBuilt(doc);
                  }}
                >
                  Upload now
                </Button>
              </span>
            ) : (
              <span>version {doc.latest.versionNo}</span>
            )}
          </Table.Cell>
        </Table.Row>
      ));
    } else {
      return (
        <Table.Row>
          <Table.Cell>No drawings found</Table.Cell>
        </Table.Row>
      );
    }
  };

  const renderItem = (index: number, text: string, value: boolean, readonly: boolean) => (
    <List.Item key={index}>
      <List.Content floated="right">
        <Form>
          <Form.Group inline style={{ marginBottom: "0.3em", marginTop: "0.3em" }}>
            <Form.Field>
              <Radio
                disabled={readonly ?? false}
                label="No"
                checked={!value}
                onChange={() => handleChange(false, index)}
              ></Radio>
            </Form.Field>
            <FormField>
              <Radio readOnly={readonly} label="Yes" checked={value} onChange={() => handleChange(true, index)}></Radio>
            </FormField>
          </Form.Group>
        </Form>
      </List.Content>
      <List.Content>{text}</List.Content>
    </List.Item>
  );

  const renderDeclarations = () => (
    <div className="content">
      <h4>I can confirm:</h4>
      <List divided>
        {asBuiltDeclarations.map((item) => renderItem(item.index, item.text, item.value, item.readonly))}
      </List>
    </div>
  );

  return (
    <>
      <Modal open={open}>
        <Modal.Header>{title}</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {showAsBuilt ? (
              <>
                {!showGisDeclaration && (
                  <Segment basic secondary>
                    <p>This permit requires as-built for drawings.</p>
                    <Table basic="very" compact>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Drawing</Table.HeaderCell>
                          <Table.HeaderCell>As-Built version</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>{renderDocTableRow()}</Table.Body>
                    </Table>
                  </Segment>
                )}
                {renderDeclarations()}
              </>
            ) : (
              <>Are you sure?</>
            )}
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress}>
            Mark as complete
          </Button>
        </Modal.Actions>
      </Modal>

      <AddNewVersionToDocumentModalContainer
        open={selectedDocumentToUploadAsBuilt !== null}
        document={selectedDocumentToUploadAsBuilt!}
        onClose={() => {
          setSelectedDocumentToUploadAsBuilt(null);
        }}
        onDocumentAdded={(doc) => {
          setSelectedDocumentToUploadAsBuilt(null);
          onAsBuiltAdded && onAsBuiltAdded();
        }}
      />
    </>
  );
};

export default CompletePermitModal;
